import { RouteComponent, RouteLocationNormalized, LocationQueryValue, LocationQueryRaw } from 'vue-router';
import { RouteFragment } from '../RouteFragment';
import { RouteParams } from '../RouteParams';
import { Degree } from '@/models/Degree';
import { RouteEmpty } from './RouteEmpty';
import { IEmbeddedStateMutable } from '@/url/IEmbeddedStateMutable';
import { NavigationTargetResult } from '../NavigationTargetResult';
import { FilterSelectionState } from '@/filters/selection/FilterSelectionState';
import { FilterKey } from '@studyportals/search-filters';
import { valueOrFirstOrNull } from '@/platform/arrayExtensions';
import { PaginationDefinition } from '@/store/PaginationDefinition';
import { QueryParametersState } from '@/url/QueryParametersState';
import { AllowedQueryParameterKeys } from '@/url/AllowedQueryParameterKeys';

export class RouteRoot extends RouteFragment {

	public static readonly fragmentType: typeof RouteRoot = RouteRoot;

	private readonly defaultParam = RouteParams.DEGREE_TYPE;

	public constructor() {
		super('Universities');
	}

	public instantiate(): RouteFragment {
		return new RouteRoot();
	}

	protected override selfRoutePath(): string {
		return `/universities/:${this.defaultParam}(${Degree.values.join('|')})?`;
	}

	protected override async selfRouteComponent(): Promise<RouteComponent> {
		return import('@/presentation/App.vue');
	}

	protected override selfSupports(): Promise<boolean> {
		return Promise.resolve(true);
	}

	protected override selfEncodeToNavigationTarget(
		navigationTargetResult: NavigationTargetResult
	): Promise<NavigationTargetResult> {
		navigationTargetResult.navigationTarget.params ??= {};
		navigationTargetResult.navigationTarget.params[this.defaultParam] =
			navigationTargetResult.remainingEmbeddedState.degreeOrNull?.value ?? null;

		return Promise.resolve(this.encodeAsQuery(navigationTargetResult));
	}

	protected override selfDecodeByPopulatingFrom(
		embeddedState: IEmbeddedStateMutable,
		destination: RouteLocationNormalized
	): Promise<void> {
		const filterSelection = this.decodeFromQuery(destination);
		embeddedState.mergeFilterSelectionWith(filterSelection);
		embeddedState.withPage(+(destination.query.page || PaginationDefinition.DEFAULT_PAGE_NUMBER));

		const queryParameters = this.decodeAdditionalParametersFromQuery(destination);
		embeddedState.withQueryParameters(queryParameters);

		const degree = Degree.from(valueOrFirstOrNull(destination.params[this.defaultParam]));
		if (degree !== null) {
			embeddedState.withDegree(degree);
		}

		return Promise.resolve();
	}

	protected override selfProvidePrototypeOfFallbacks(): RouteFragment[] {
		return [new RouteEmpty()];
	}

	private decodeFromQuery(destination: RouteLocationNormalized): FilterSelectionState {
		const query = destination.query;
		const nonFilterKeys = ['page', 'bot'];

		let filterSelection = new FilterSelectionState();
		for (const filterKey in query) {
			if (nonFilterKeys.includes(filterKey)) {
				continue;
			}

			const values = this.normalizeQueryValue(query[filterKey]);

			filterSelection = filterSelection.replace(
				filterKey as FilterKey,
				values.map((v) => v?.toString() ?? '')
			);
		}

		return filterSelection;
	}

	private decodeAdditionalParametersFromQuery(destination: RouteLocationNormalized): QueryParametersState {
		const query = destination.query;
		let selection = new QueryParametersState();

		for (const key in query) {
			const values = this.normalizeQueryValue(query[key]);

			selection = selection.change(
				key as AllowedQueryParameterKeys,
				values.map((v) => v?.toString() ?? '')[0]
			);
		}

		return selection;
	}

	private normalizeQueryValue(value: LocationQueryValue | LocationQueryValue[]): LocationQueryValue[] {
		return Array.isArray(value) ? value : [value];
	}

	private encodeAsQuery(navigationTargetResult: NavigationTargetResult): NavigationTargetResult {
		const embeddedState = navigationTargetResult.remainingEmbeddedState;

		const query = {};

		const keysOfSelectedFilters = embeddedState.filterSelectionState.reactive.identifySelectedFilterKeys();
		for (const filterKey of keysOfSelectedFilters) {
			const filterSelectionState = embeddedState.filterSelectionState;
			query[filterKey] = filterSelectionState.reactive.getFor(filterKey);

			embeddedState.withFilterSelection(filterSelectionState.clearBy(filterKey));
		}

		const parameterKeys = embeddedState.queryParameters.identifySelectedParameters();
		for (const key of parameterKeys) {
			const additionalSelection = embeddedState.queryParameters;
			query[key] = additionalSelection.getFor(key);

			embeddedState.withQueryParameters(embeddedState.queryParameters);
		}

		navigationTargetResult.navigationTarget.query = query;
		this.addPageToQuery(query, embeddedState);

		return navigationTargetResult;
	}

	private addPageToQuery(query: LocationQueryRaw, embeddedState: IEmbeddedStateMutable): void {
		if (embeddedState.pageOrNull !== null && embeddedState.pageOrNull !== Number(PaginationDefinition.DEFAULT_PAGE_NUMBER)) {
			query['page'] = embeddedState.pageOrNull;
		}
	}
}
